import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { format, startOfDay, endOfDay, parseISO } from "date-fns";
import {
  convertLocalDateToUTCRange,
  formatUTCDateForLocalTime,
} from "@/utils/timeFormatUtils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { useToast } from "@/hooks/use-toast";
import { reportService } from "@/services/reportService";
import {
  PaymentCheckReportDTO,
  PaymentCheckReportFilter,
  PaymentCheckReportResponse,
  CheckStatus,
  RecipientType,
} from "@/types/reports";
import {
  Filter,
  Download,
  RefreshCw,
  User,
  Building,
  FileText,
  Calendar,
  DollarSign,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { BackLink } from "@/components/brand/BackLink";

export default function PaymentCheckReport() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [filters, setFilters] = useState<PaymentCheckReportFilter>({
    statuses: [CheckStatus.CREATED, CheckStatus.PAID], // Default both statuses selected
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState("checkNumber");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [localFilters, setLocalFilters] = useState({
    recipientNameSearch: "",
    checkNumber: "",
    issueDateFrom: "",
    issueDateTo: "",
    createdDateFrom: "",
    createdDateTo: "",
    statuses: [CheckStatus.CREATED, CheckStatus.PAID], // Default both statuses selected
  });

  const statusOptions = [
    { value: CheckStatus.CREATED, label: "Created" },
    { value: CheckStatus.PAID, label: "Paid" },
  ];

  // Fetch payment check report data
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "payment-check-report",
      filters,
      currentPage,
      pageSize,
      sortField,
      sortDirection,
    ],
    queryFn: () =>
      reportService.fetchPaymentCheckReport(
        filters,
        currentPage,
        pageSize,
        `${sortField},${sortDirection}`,
      ),
    enabled: Object.keys(filters).length > 0,
    retry: false,
  });

  const handleLocalFilterChange = (key: string, value: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleStatusFilterChange = (status: CheckStatus, checked: boolean) => {
    setLocalFilters((prev) => {
      const currentStatuses = prev.statuses || [];
      const newStatuses = checked
        ? [...currentStatuses, status]
        : currentStatuses.filter((s) => s !== status);

      return {
        ...prev,
        statuses: newStatuses,
      };
    });
  };

  const applyFilters = () => {
    const newFilters: PaymentCheckReportFilter = {};

    // Text filters
    if (localFilters.recipientNameSearch.trim()) {
      newFilters.recipientNameSearch = localFilters.recipientNameSearch.trim();
    }

    if (localFilters.checkNumber.trim()) {
      const checkNum = parseInt(localFilters.checkNumber.trim());
      if (!isNaN(checkNum)) {
        newFilters.checkNumber = checkNum;
      }
    }

    // Date filters: the browser's local day, sent as UTC instants
    if (localFilters.issueDateFrom) {
      const range = convertLocalDateToUTCRange(localFilters.issueDateFrom);
      newFilters.issueDateFrom = range.from;
    }

    if (localFilters.issueDateTo) {
      const range = convertLocalDateToUTCRange(localFilters.issueDateTo);
      newFilters.issueDateTo = range.to;
    }

    if (localFilters.createdDateFrom) {
      const range = convertLocalDateToUTCRange(localFilters.createdDateFrom);
      newFilters.createdDateFrom = range.from;
    }

    if (localFilters.createdDateTo) {
      const range = convertLocalDateToUTCRange(localFilters.createdDateTo);
      newFilters.createdDateTo = range.to;
    }

    // Handle status filters
    if (localFilters.statuses && localFilters.statuses.length > 0) {
      newFilters.statuses = localFilters.statuses;
    }

    setFilters(newFilters);
    setCurrentPage(0);
  };

  const resetFilters = () => {
    const defaultFilters = {
      statuses: [CheckStatus.CREATED, CheckStatus.PAID], // Restore default both statuses selected
    };
    setFilters(defaultFilters);
    setLocalFilters({
      recipientNameSearch: "",
      checkNumber: "",
      issueDateFrom: "",
      issueDateTo: "",
      createdDateFrom: "",
      createdDateTo: "",
      statuses: [CheckStatus.CREATED, CheckStatus.PAID], // Restore default both statuses selected
    });
    setCurrentPage(0);
    setSortField("checkNumber");
    setSortDirection("desc");
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(0);
  };

  // Pagination controls
  const totalPages = reportData?.page?.totalPages || 0;
  const totalElements = reportData?.page?.totalElements || 0;
  const numberOfElements = reportData?.content?.length || 0;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const [isExporting, setIsExporting] = useState(false);
  const exportReport = async () => {
    setIsExporting(true);
    try {
      // Build export filters from current localFilters to ensure UI matches export
      const exportFilters: PaymentCheckReportFilter = {};

      // Text filters
      if (localFilters.recipientNameSearch.trim()) {
        exportFilters.recipientNameSearch =
          localFilters.recipientNameSearch.trim();
      }

      if (localFilters.checkNumber.trim()) {
        const checkNum = parseInt(localFilters.checkNumber.trim());
        if (!isNaN(checkNum)) {
          exportFilters.checkNumber = checkNum;
        }
      }

      // Date filters: the browser's local day, sent as UTC instants
      if (localFilters.issueDateFrom) {
        const range = convertLocalDateToUTCRange(localFilters.issueDateFrom);
        exportFilters.issueDateFrom = range.from;
      }

      if (localFilters.issueDateTo) {
        const range = convertLocalDateToUTCRange(localFilters.issueDateTo);
        exportFilters.issueDateTo = range.to;
      }

      if (localFilters.createdDateFrom) {
        const range = convertLocalDateToUTCRange(localFilters.createdDateFrom);
        exportFilters.createdDateFrom = range.from;
      }

      if (localFilters.createdDateTo) {
        const range = convertLocalDateToUTCRange(localFilters.createdDateTo);
        exportFilters.createdDateTo = range.to;
      }

      // Handle status filters
      if (localFilters.statuses && localFilters.statuses.length > 0) {
        exportFilters.statuses = localFilters.statuses;
      }

      await reportService.exportPaymentCheckReport(exportFilters);
      toast({
        title: "Success",
        description: "Report exported successfully!",
        variant: "default",
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast({
        title: "Error",
        description: "Failed to export report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="w-4 h-4" />
    ) : (
      <ArrowDown className="w-4 h-4" />
    );
  };

  const getStatusBadge = (status: CheckStatus) => {
    const variant = status === CheckStatus.PAID ? "default" : "secondary";
    const color =
      status === CheckStatus.PAID
        ? "text-success bg-success/10"
        : "text-warning bg-warning/10";

    return (
      <Badge variant={variant} className={color}>
        {status === CheckStatus.PAID ? "Paid" : "Created"}
      </Badge>
    );
  };

  const getRecipientTypeIcon = (type: RecipientType) => {
    return type === RecipientType.EMPLOYEE ? (
      <User className="w-4 h-4 text-info" />
    ) : (
      <Building className="w-4 h-4 text-success" />
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <BackLink to="/admin/reports">Back to Reports</BackLink>
      </div>

      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-foreground">
          Payment Check Report
        </h1>
        <p className="text-foreground">
          View and analyze recently issued payment checks with comprehensive
          filtering
        </p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
          <CardDescription>
            Filter payment checks by recipient, dates, status, and more
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="recipientSearch">Recipient Name</Label>
              <Input
                id="recipientSearch"
                placeholder="Search by recipient name..."
                value={localFilters.recipientNameSearch}
                onChange={(e) =>
                  handleLocalFilterChange("recipientNameSearch", e.target.value)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="checkNumber">Check Number</Label>
              <Input
                id="checkNumber"
                type="number"
                placeholder="Enter exact check number..."
                value={localFilters.checkNumber}
                onChange={(e) =>
                  handleLocalFilterChange("checkNumber", e.target.value)
                }
              />
            </div>
          </div>

          {/* Date Filters */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">
                Issue Date Range
              </Label>
              <p className="text-sm text-muted-foreground mb-2">
                Filter by when checks were issued (your local time)
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issueDateFrom">From</Label>
                  <Input
                    id="issueDateFrom"
                    type="date"
                    value={localFilters.issueDateFrom}
                    onChange={(e) =>
                      handleLocalFilterChange("issueDateFrom", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="issueDateTo">To</Label>
                  <Input
                    id="issueDateTo"
                    type="date"
                    value={localFilters.issueDateTo}
                    onChange={(e) =>
                      handleLocalFilterChange("issueDateTo", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-base font-semibold">
                Created Date Range
              </Label>
              <p className="text-sm text-muted-foreground mb-2">
                Filter by when checks were created (your local time)
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="createdDateFrom">From</Label>
                  <Input
                    id="createdDateFrom"
                    type="date"
                    value={localFilters.createdDateFrom}
                    onChange={(e) =>
                      handleLocalFilterChange("createdDateFrom", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="createdDateTo">To</Label>
                  <Input
                    id="createdDateTo"
                    type="date"
                    value={localFilters.createdDateTo}
                    onChange={(e) =>
                      handleLocalFilterChange("createdDateTo", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status Filter */}
          <div className="space-y-2">
            <Label>Check Status</Label>
            <div className="flex flex-wrap gap-4">
              {statusOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${option.value}`}
                    checked={
                      localFilters.statuses?.includes(option.value) || false
                    }
                    onCheckedChange={(checked) =>
                      handleStatusFilterChange(option.value, !!checked)
                    }
                  />
                  <Label
                    htmlFor={`status-${option.value}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 pt-4 border-t">
            <Button onClick={applyFilters} className="flex-1 md:flex-none">
              <Search className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
            <Button
              onClick={exportReport}
              disabled={isExporting || !reportData?.content.length}
              variant="outline"
              className="flex-1 md:flex-none"
            >
              {isExporting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export Excel
            </Button>
            <Button
              onClick={resetFilters}
              variant="ghost"
              className="flex-1 md:flex-none"
            >
              Reset Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {error && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive py-8">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Error loading payment check data. Please try again.</p>
              <Button
                onClick={() => refetch()}
                className="mt-4"
                variant="outline"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-foreground" />
              <p className="text-foreground">Loading payment check data...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {reportData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Payment Check Results</CardTitle>
                <CardDescription>
                  Showing {numberOfElements} of {totalElements} checks
                  {totalElements > 0 && (
                    <>
                      {" "}
                      (Page {currentPage + 1} of {totalPages})
                    </>
                  )}
                </CardDescription>
              </div>

              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <Label htmlFor="pageSize" className="text-sm">
                  Per page:
                </Label>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value));
                    setCurrentPage(0);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {reportData.content.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort("checkNumber")}
                            className="h-auto p-0 font-semibold"
                          >
                            Check #{getSortIcon("checkNumber")}
                          </Button>
                        </TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort("status")}
                            className="h-auto p-0 font-semibold"
                          >
                            Status
                            {getSortIcon("status")}
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort("totalAmount")}
                            className="h-auto p-0 font-semibold"
                          >
                            Amount
                            {getSortIcon("totalAmount")}
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort("issueDate")}
                            className="h-auto p-0 font-semibold"
                          >
                            Issue Date
                            {getSortIcon("issueDate")}
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort("createdDate")}
                            className="h-auto p-0 font-semibold"
                          >
                            Created Date
                            {getSortIcon("createdDate")}
                          </Button>
                        </TableHead>
                        <TableHead>Batch ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.content.map((check) => (
                        <TableRow key={check.checkId}>
                          <TableCell className="font-mono">
                            {check.checkNumber}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getRecipientTypeIcon(check.recipientType)}
                              <div>
                                <div className="font-medium">
                                  {check.recipientName}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {check.recipientType ===
                                  RecipientType.EMPLOYEE
                                    ? "Employee"
                                    : "Partner"}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(check.status)}</TableCell>
                          <TableCell className="font-mono">
                            <div className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {check.totalAmount.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              {formatUTCDateForLocalTime(check.issueDate)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              {formatUTCDateForLocalTime(check.createdDate)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Link
                              to={`/admin/check-batches/${check.checkBatchId}`}
                              className="text-info hover:text-info underline font-mono text-sm"
                            >
                              {check.checkBatchId}
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                  No payment checks found
                </h3>
                <p className="text-muted-foreground">
                  Try adjusting your filters or check if there are any payment
                  checks in the system.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
